import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Award, BarChart3, Bell, ChevronLeft, Clock, FlaskConical, GraduationCap, Inbox, KeyRound, Mail, Menu, Moon, Sun, TrendingUp } from "lucide-react";
import { apiGet, apiPost, getApiSettings, saveApiSettings, endSession, returnToOwnCompany, unpackList } from "./Api/Client.js";
import { setCache } from "./Api/Cache.js";
import { fetchAllPages } from "./Api/allPages.js";
import { leaveSupport } from "./Api/leaveSupport.js";
import { claimKeys, isLatest, pollIfVisible } from "./Api/loadVersions.js";
import { safeAppRoute } from "./Api/safeAppRoute.js";
import { LoginScreen, ProfileScreen } from "./Platform/AuthScreens.jsx";
import { CultureCheckScreen } from "./Products/hiring/CultureCheckScreen.jsx";
import { JoiningScreen } from "./Products/hiring/JoiningScreen.jsx";
import { RouteRenderer } from "./Platform/AppScreens.jsx";
import { useSuggestedTasksMenu, withSuggestedTasksAccess } from "./Products/axon/index.js";
import { AdminPanelScreen } from "./Platform/admin/AdminPanelScreen.jsx";
import { pickLoadingLine, pickLoadingEmoji } from "./loadingLines.js";

import { resolveActiveEmployee } from "./Shared/ScreenUtils.jsx"; // Forced Reload
import {
  ATGLogo,
  IconHome,
  IconHRMS,
  IconLMS,
  IconProjects,
  IconSalesGuide,
  IconCalendar,
  IconPassword,
  IconBank,
  IconPayslip,
  IconManageEmployees,
  IconDocument,
  IconFinance,
  IconNewEmployee,
  IconAdd,
  IconDot,
  IconDelayManagement,
  SvgChevronUp,
  SvgChevronDown,
} from "./Components/icons/icons.jsx";
import { canRead, canSeeLabel, hasPageAccess } from "./Services/pageAccess.js";

// ΓöÇΓöÇΓöÇ Flat Nav List For Page Title Lookup ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
const navItems = [
  { label: "Home",                  path: "/home/" },
  { label: "HRMS",                  path: "/hrms/" },
  { label: "Sales CRM",                   path: "/lms/" },
  { label: "Projects",              path: "/projects/" },
  { label: "Project Management",        path: "/projects/management/" },
  { label: "Apply Leave",           path: "/leave/apply/" },
  { label: "Bank Details",          path: "/Bankdetails/" },
  { label: "Payslips",              path: "/payslips/" },
  { label: "Employee Register",     path: "/employee-register/" },
  { label: "Send Offer Letter",     path: "/Onboard/Send_Offer" },
  { label: "IDs Waiting",           path: "/joining-reviews/" },
  { label: "Send Certificate",      path: "/send-certificate" },
  { label: "Deactivate Employee",   path: "/deactivate-multiple-employee/" },
  { label: "How we work",          path: "/docs/company/" },
  { label: "Intranet",              path: "/docs/intranet/" },
  { label: "InterviewGod",          path: "/docs/interviewgod/" },
  { label: "Banao Technologies",    path: "/docs/banao/" },
  { label: "Vikaas",                path: "/docs/vikaas/" },
  { label: "Vidya",                 path: "/docs/vidya/" },
  { label: "Finance",               path: "/payments/" },
  { label: "P&L",                   path: "/pnl/" },
  { label: "Notifications",         path: "/notifications/" },
  { label: "Integrations",          path: "/integrations/" },
  { label: "AXON API",              path: "/axon-api/" },
  { label: "Suggested Tasks",       path: "/proposals/" },
  { label: "Admin Panel",           path: "/company-admin/" },
  { label: "Audit",                 path: "/audit/" },
  { label: "Companies",             path: "/platform/" },
  { label: "Delay Management",      path: "/delays/" },
  { label: "Vibe testing (Avalok)", path: "/vibe-testing/" },
  { label: "Growth Roadmap",        path: "/growth-roadmap/" },
  { label: "Sales Pipeline",        path: "/lms/analytics/" },
  { label: "Sales Performance",     path: "/sales-performance/" },
];

const endpointMap = [
  ["me", "/Users/Auth/Me/", "object"],
  ["employees", "/Users/EmployeeProfiles/?page_size=1000", "list"],
  ["departments", "/Users/Departments/?page_size=1000", "list"],
  ["subDepartments", "/Users/SubDepartments/?page_size=1000", "list"],
  ["positions", "/Users/Positions/", "list"],
  ["skills", "/Users/Skills/", "list"],
  ["userSkills", "/Users/UserSkills/", "list"],
  ["payProfiles", "/Users/PayProfiles/", "list"],
  ["bankAccounts", "/Users/EmployeeBankAccounts/", "list"],
  ["paymentSnapshots", "/Users/EmployeePaymentSnapshots/", "list"],
  ["leavePolicies", "/Users/LeavePolicies/", "list"],
  ["leaveBalances", "/Users/LeaveBalances/", "list"],
  ["projects", "/Project/ProjectWorkspaces/", "list"],
  ["teamAssignments", "/Project/TeamAssignments/", "list"],
  ["repositories", "/Project/RepositoryLinks/", "list"],
  ["tasks", "/TasksDashboard/WorkItems/", "list"],
  ["workEntries", "/TasksDashboard/WorkEntries/", "list"],
  ["dailyStatus", "/TasksDashboard/DailyStatusEntries/", "list"],
  ["taskActivities", "/TasksDashboard/TaskActivities/", "list"],
  ["notifications", "/MainApp/Notifications/", "list"],
  ["leaveRequests", "/MainApp/LeaveRequests/", "list"],
  ["leaveOverview", "/MainApp/leave/", "object"],
  ["offers", "/MainApp/OnboardingOffers/", "list"],
  ["issues", "/MainApp/ExternalIssueReferences/", "list"],
  ["leadAccounts", "/Banao/LeadAccounts/", "list"],
  ["leadTags", "/Banao/LeadTags/", "list"],
  ["leadContacts", "/Banao/LeadContacts/", "list"],
  ["leadActivities", "/Banao/LeadActivities/", "list"],
  ["leadNotes", "/Banao/LeadNotes/", "list"],
  ["leadTests", "/Banao/LeadTests/", "list"],
  ["leadProposals", "/Banao/ProposalArtifacts/", "list"],
  ["leadAudits", "/Banao/AuditArtifacts/", "list"],
  ["leadTransitions", "/Banao/WorkflowTransitions/", "list"],
  ["lmsLeads", "/Lms/api/leads/?limit=1", "object"],
  ["learningPaths", "/Lms/LearningPaths/", "list"],
  ["learningModules", "/Lms/LearningModules/", "list"],
  ["learningAssignments", "/Lms/LearningAssignments/", "list"],
  ["leadQueueSnapshots", "/Lms/LeadQueueSnapshots/", "list"],
  ["revenueSnapshots", "/Lms/RevenuePerformanceSnapshots/", "list"],
  ["financeDashboard", "/FinanceAndPayroll/payments/?pay_month=current", "object"],
  ["payPeriods", "/FinanceAndPayroll/PayPeriods/", "list"],
  ["payrollRuns", "/FinanceAndPayroll/PayrollRuns/", "list"],
  ["payrollLineItems", "/FinanceAndPayroll/PayrollLineItems/", "list"],
  ["payslipDocuments", "/FinanceAndPayroll/PayslipDocuments/", "list"],
  ["domains", "/Users/Domains/?page_size=1000", "list"],
  ["departmentMemberships", "/Users/DepartmentMemberships/?page_size=1000", "list"],
  ["userStatusSnapshots", "/Users/UserStatusSnapshots/", "list"],
  ["benchPeriods", "/Users/BenchPeriods/", "list"],
  ["employeeRatings", "/Users/EmployeeRatings/", "list"],
  ["employeeCertificates", "/Users/EmployeeCertificates/", "list"],
  ["leaveTransactions", "/Users/LeaveTransactions/", "list"],
  ["resignationRequests", "/Users/ResignationRequests/", "list"],
  ["userEffortReports", "/Users/UserEffortReports/", "list"],
  ["interviewProgress", "/Users/InterviewProgress/", "list"],
  ["credentialVaultItems", "/MainApp/CredentialVaultItems/", "list"],
  ["credentialShareGrants", "/MainApp/CredentialShareGrants/", "list"],
  ["notificationSnoozeRecords", "/MainApp/NotificationSnoozeRecords/", "list"],
  ["managerScopes", "/MainApp/ManagerScopes/", "list"],
  ["complianceCampaigns", "/Project/ComplianceCampaigns/", "list"],
  ["complianceAssignments", "/Project/ComplianceAssignments/", "list"],
  ["delays", "/Project/ProjectDelays/", "list"],
  ["slackThreads", "/TasksDashboard/SlackDeliveryThreads/", "list"],
  ["slackMessages", "/TasksDashboard/SlackDeliveryMessages/", "list"],
  ["externalWorkMappings", "/TasksDashboard/ExternalWorkMappings/", "list"],
  ["managerAbbreviations", "/TasksDashboard/ManagerAbbreviations/", "list"],
  ["leadStatusHistory", "/Banao/WorkflowStatusHistory/", "list"],
  ["compensationPlans", "/FinanceAndPayroll/CompensationPlans/", "list"],
  ["financeBankAccounts", "/FinanceAndPayroll/BankAccounts/", "list"],
  ["approvalDecisions", "/FinanceAndPayroll/ApprovalDecisions/", "list"],
  ["gitRepoSnapshots", "/Git/GitRepositorySnapshots/", "list"],
  ["gitActivitySnapshots", "/Git/GitActivitySnapshots/", "list"],
  ["repoUtilityRequests", "/Git/RepositoryUtilityRequests/", "list"],
  ["githubRepositories", "/GithubExtension/GitHubRepositories/", "list"],
  ["branchReviewers", "/GithubExtension/BranchReviewerAssignments/", "list"],
  ["branchTesters", "/GithubExtension/BranchTestingAssignments/", "list"],
  ["repoBranchStatuses", "/GithubExtension/RepositoryBranchStatuses/", "list"],
  ["templateVariables", "/HtmlTemplate/TemplateVariables/", "list"],
  ["offerMacros", "/HtmlTemplate/OfferMacros/", "list"],
  ["contentTemplates", "/HtmlTemplate/ContentTemplates/", "list"],
  ["offerTemplates", "/HtmlTemplate/OfferTemplates/", "list"],
  ["genericHtmlTemplates", "/HtmlTemplate/GenericHtmlTemplates/", "list"],
  ["integrationProviders", "/IntegrationHub/IntegrationProviders/", "list"],
  ["integrationConnections", "/IntegrationHub/IntegrationConnections/", "list"],
  ["webhookInboxEvents", "/IntegrationHub/WebhookInboxEvents/", "list"],
  ["integrationSyncJobs", "/IntegrationHub/IntegrationSyncJobs/", "list"],
  ["integrationAttempts", "/IntegrationHub/IntegrationAttempts/", "list"],
  ["agentPrincipals", "/McpAccessLayer/AgentPrincipals/", "list"],
  ["mcpToolDefinitions", "/McpAccessLayer/McpToolDefinitions/", "list"],
  ["mcpResourceDefinitions", "/McpAccessLayer/McpResourceDefinitions/", "list"],
  ["mcpAccessGrants", "/McpAccessLayer/McpAccessGrants/", "list"],
  ["mcpInvocationAudits", "/McpAccessLayer/McpInvocationAudits/", "list"],
  ["draftAgentActions", "/McpAccessLayer/DraftAgentActions/", "list"],
  ["legacyApplicationMaps", "/LegacyBridge/LegacyApplicationMaps/", "list"],
  ["legacyModelCrosswalks", "/LegacyBridge/LegacyModelCrosswalks/", "list"],
  ["migrationRuns", "/LegacyBridge/MigrationRuns/", "list"],
  ["legacyMigrationIssues", "/LegacyBridge/LegacyMigrationIssues/", "list"],
  ["enterpriseTenants", "/EnterpriseCore/Tenants/", "list"],
  ["enterpriseOrganizations", "/EnterpriseCore/Organizations/", "list"],
  ["enterpriseBusinessUnits", "/EnterpriseCore/BusinessUnits/", "list"],
  ["enterpriseWorkspaces", "/EnterpriseCore/Workspaces/", "list"],
  ["enterpriseRoles", "/EnterpriseCore/Roles/", "list"],
  ["enterpriseRoleAssignments", "/EnterpriseCore/RoleAssignments/", "list"],
  ["accessAuditLogs", "/EnterpriseCore/AccessAuditLogs/", "list"],
];



// ΓöÇΓöÇΓöÇ Permission Map: Nav Label -> Required Capabilities ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ

// Retained only as documentation of the pre-switchover rules. The live gate is
// the page-access grid — see Frontend/src/Services/pageAccess.js.

// ─── Loading screen: a pulsing emoji (not a spinner) + a context-aware hype line ───
// Lines live in loadingLines.js and are picked by path (welcome / sales / general).
function LoadingView() {
  const [pick] = useState(() => ({
    emoji: pickLoadingEmoji(),
    caption: pickLoadingLine(typeof window !== "undefined" ? window.location.pathname : ""),
  }));
  return (
    <div className="Loading-Wrap">
      <div className="Loading-Emoji" aria-hidden="true">{pick.emoji}</div>
      <span className="Loading-Title">Loading…</span>
      <span className="Loading-Caption">{pick.caption}</span>
    </div>
  );
}

// A nav item is visible when the user can read its page. The grid comes from the
// server on /me, so the sidebar and the API agree by construction. The manager
// exception on Leave Requests is resolved server-side and arrives already applied.
function userCanSee(label, me) {
  return canSeeLabel(me, label);
}

// The Audit menu entry (S9). Opened by an auditor engagement, not by a page grant, so it
// is not one of the page-grant items below and not in LABEL_TO_PAGE.
function auditNav() {
  return [{ isSection: true, label: "Audit" }, { label: "Audit", icon: <KeyRound size={20} strokeWidth={1.6} />, path: "/audit/" }];
}

// An old address that moved: go to the new one once, after render. It replaces the old entry
// rather than adding one (Entry 20 review): with a push, Back landed on the old address, which
// redirected forward again, so the person could never go back past it.
function MovedTo({ navigate, to }) {
  useEffect(() => { navigate(to, { replace: true }); }, [navigate, to]);
  return null;
}

// The operator console (Entry 20, design A): the platform's own staff only, never a page grant,
// so it is not one of the page-grant items below and not in LABEL_TO_PAGE.
function platformNav() {
  return [{ isSection: true, label: "Platform" }, { label: "Companies", icon: <KeyRound size={20} strokeWidth={1.6} />, path: "/platform/" }];
}

// ΓöÇΓöÇΓöÇ Nav Structure ΓÇö Maps Figma Items To Your Existing Paths ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
function buildNavItems(activePath, me, integrationsDown = false, idsWaiting = false, hasActiveDelay = false, calendarReconnect = false) {
  // Nav is grouped into sections by function. A section only renders when the
  // current user can see at least one item in it — so the visible sections
  // double as a hint of what this user's role/capabilities unlock.
  // Audit portal (S9): an auditor-only account (no employee record) sees nothing else.
  if (me?.auditorOnly) return auditNav();
  const sections = [
    { label: null, items: [
      { label: "Home",              icon: <IconHome active={activePath === "/home/"} />, path: "/home/" },
    ] },
    { label: "Sales", items: [
      { label: "Sales CRM",         icon: <IconLMS />,             path: "/lms/" },
      { label: "Sales CRM ✨",       icon: <IconLMS />,             path: "/crm/" },
      { label: "Sales Pipeline",    icon: <BarChart3 size={20} strokeWidth={1.6} />, path: "/lms/analytics/" },
      { label: "SDR Onboarding", icon: <GraduationCap size={20} strokeWidth={1.6} />, path: "/junior-onboarding/" },
      { label: "AE Onboarding",  icon: <GraduationCap size={20} strokeWidth={1.6} />, path: "/sales-onboarding/" },
      { label: "Playbook",          icon: <IconSalesGuide />,      path: "/playbook/" },
      { label: "RevOps",            icon: <BarChart3 size={20} strokeWidth={1.6} />, path: "/revops/" },
      { label: "RevOps V2", quiet: true, icon: <BarChart3 size={14} strokeWidth={1.6} />, path: "/revops-v2/" },
    ] },
    // New 2026-10-07 — BA Performance split out of the old "Analytics & Perf"
    // page, paired here with Growth Roadmap (moved out of "My Space") because
    // that is how the industry research behind this split consistently
    // structures the function: goal-setting, review, and performance tracking
    // as their own department, not a tab buried inside pipeline reporting.
    { label: "Performance & Goals", items: [
      { label: "Sales Performance", icon: <Award size={20} strokeWidth={1.6} />, path: "/sales-performance/" },
      { label: "Growth Roadmap",    icon: <TrendingUp size={20} strokeWidth={1.6} />, path: "/growth-roadmap/" },
    ] },
    { label: "Delivery", items: [
      { label: "Projects",          icon: <IconProjects />,        path: "/projects/" },
      { label: "Project Management",    icon: <IconProjects />,        path: "/projects/management/" },
      {
        label: "Delay Management",
        // Whole item turns red while any delay is Active, not just a corner
        // dot — this is the one thing on the page that's supposed to be
        // impossible to miss from the sidebar.
        icon: <Clock size={20} color={hasActiveDelay ? "var(--red-600)" : "var(--c-500)"} absoluteStrokeWidth />,
        path: "/delays/",
        urgent: hasActiveDelay,
      },
    ] },
    { label: "Development", items: [
      { label: "DevOps", icon: <KeyRound size={20} strokeWidth={1.6} />, path: "/devops/" },
      { label: "Vibe testing (Avalok)", icon: <FlaskConical size={20} strokeWidth={1.6} />, path: "/vibe-testing/" },
    ] },
    // Each of these is its own page with its own access rule, so each sits at
    // the top level rather than behind a dropdown that hides whether you have
    // access until you open it.
    { label: "People & HR", items: [
      { label: "HRMS",                icon: <IconHRMS />,             path: "/hrms/" },
      { label: "Leave Requests",      icon: <IconCalendar />,         path: "/leave-requests/" },
      { label: "Send Offer Letter",   icon: <IconNewEmployee />,      path: "/Onboard/Send_Offer" },
      { label: "IDs Waiting",         icon: <IconManageEmployees />,  path: "/joining-reviews/", badge: idsWaiting },
      { label: "Send Certificate",    icon: <IconDocument />,         path: "/send-certificate" },
      { label: "Deactivate Employee", icon: <IconManageEmployees />,  path: "/deactivate-multiple-employee/" },
    ] },
    { label: "Finance", items: [
      { label: "Finance", icon: <IconFinance />,        path: "/payments/?pay_month=current" },
      { label: "P&L", icon: <BarChart3 size={18} />,    path: "/pnl/" },
      { label: "Employee Register", icon: <IconManageEmployees />, path: "/employee-register/" },
    ] },
    // Its own top-level section, not nested under Delivery — it's company-wide
    // reference material (every project's own repo, not just delivery's).
    // One project per sidebar link, by explicit instruction (2026-10-07) —
    // the old single "Documents" item hid the per-project split behind an
    // in-page dropdown; now you can see all five are there without clicking
    // in first. Each opens DocsPortalScreen pre-selected to that project via
    // its own path segment (/docs/<project>/, not a ?project= query param —
    // see DocsPortalScreen.jsx for why that changed 2026-10-07). Each is also
    // its own access-controlled page now (pages.py "documentation_<project>"),
    // not one shared "documentation" gate for all five — granting one
    // project's docs no longer opens the other four.
    { label: "Documents", items: [
      { label: "How we work",         icon: <IconDocument />,        path: "/docs/company/" },
      { label: "Intranet",            icon: <IconDocument />,        path: "/docs/intranet/" },
      { label: "InterviewGod",        icon: <IconDocument />,        path: "/docs/interviewgod/" },
      { label: "Banao Technologies",  icon: <IconDocument />,        path: "/docs/banao/" },
      { label: "Vikaas",              icon: <IconDocument />,        path: "/docs/vikaas/" },
      { label: "Vidya",               icon: <IconDocument />,        path: "/docs/vidya/" },
    ] },
    { label: "My Space", items: [
      { label: "Apply Leave",       icon: <IconCalendar />,        path: "/leave/apply/" },
      { label: "Bank Details",      icon: <IconBank />,            path: "/Bankdetails/" },
      { label: "Payslips",          icon: <IconPayslip />,         path: "/payslips/" },
      { label: "Integrations",      icon: <Mail size={20} strokeWidth={1.6} />, path: "/integrations/", badge: calendarReconnect },
      // "AXON API" — one page, one name. This label, LABEL_TO_PAGE in
      // Services/pageAccess.js, and nav_label in the backend page registry must
      // stay byte-identical; test_nav_labels_match.py fails the build if they drift.
      { label: "AXON API",          icon: <KeyRound size={20} strokeWidth={1.6} />, path: "/axon-api/" },
    ] },
    // What the AI helpers propose, waiting for a person's yes or no. Absent unless the server
    // can reach it and the person was given the page (Services/pageAccess.js).
    { label: "AI helpers", items: [
      { label: "Suggested Tasks",   icon: <Inbox size={20} strokeWidth={1.6} />, path: "/proposals/" },
    ] },
    // Entry 20, design A: the company admin's panel (people, access, modules and add-ons,
    // company settings). Only for this company's named admins; no plans or prices in it.
    ...(me?.user?.isCompanyAdmin ? [{ label: "Company", items: [
      { label: "Admin Panel",       icon: <IconManageEmployees />, path: "/company-admin/", badge: integrationsDown },
    ] }] : []),
  ];

  const visibleFor = (item) => {
    if (item.children) {
      const filteredChildren = item.children.filter((child) => userCanSee(child.label, me));
      return filteredChildren.length ? { ...item, children: filteredChildren } : null;
    }
    return userCanSee(item.label, me) ? item : null;
  };

  const out = [];
  for (const section of sections) {
    const items = section.items.map(visibleFor).filter(Boolean);
    if (!items.length) continue;
    if (section.label) out.push({ isSection: true, label: section.label });
    out.push(...items);
  }
  // Audit portal (S9): shown to auditors with an active engagement, never by page grant.
  if (me?.isAuditor) out.push(...auditNav());
  if (me?.user?.isOperator) out.push(...platformNav());
  return out;
}

// ΓöÇΓöÇΓöÇ Nav Link ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
function NavLink({ item, depth = 0, activePath, activeRoute, navigate, collapsed }) {
  const hasChildren = item.children && item.children.length > 0;

  const isGroupActive = activePath === item.path ||
    (item.path && item.path !== "/home/" &&
      activePath.startsWith(item.path.split("?")[0].replace(/\/$/, "")));

  const [open, setOpen] = useState(
    item.label === "Projects" || isGroupActive
  );

  useEffect(() => {
    if (isGroupActive && hasChildren) setOpen(true);
  }, [activePath, isGroupActive, hasChildren]);

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            display: "flex", alignItems: "center", gap: 12,
            width: "100%", paddingRight: 12, paddingTop: 8, paddingBottom: 8,
            borderRadius: 13.6, border: "none", background: "transparent",
            cursor: "pointer", textAlign: "left",
            paddingLeft: depth === 0 ? 12 : 32,
            transition: "Background 0.12s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "var(--blue-50)"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          {item.icon && <span style={{ flexShrink: 0, display: "flex" }}>{item.icon}</span>}
          {!collapsed && (
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500, lineHeight: "20px", color: "var(--c-500)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {item.label}
            </span>
          )}
          {!collapsed && (
            <span style={{ flexShrink: 0, display: "flex" }}>
              {open ? <SvgChevronUp /> : <SvgChevronDown />}
            </span>
          )}
        </button>
        {open && !collapsed && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {item.children.map((child) => (
              <NavLink key={child.path || child.label} item={child} depth={1} activePath={activePath} activeRoute={activeRoute} navigate={navigate} collapsed={collapsed} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Leaf Item ΓÇö Determine Active State
  // Several Documents links all resolve to the same bare "/docs/" path and
  // differ only by ?project= — plain prefix matching on the path alone lit
  // up all three together, because the query string (the only thing that
  // actually tells them apart) was stripped before it ever reached here.
  // Rule: if the item names its own query string, it must match the full
  // route exactly, query included. If it doesn't, it only falls back to
  // prefix matching when the CURRENT route also carries no query — once the
  // route has one, only an item that explicitly matches it (or explicitly
  // has none of its own) should claim to be "current".
  const pathBase = item.path ? item.path.split("?")[0] : "";
  const hasOwnQuery = item.path && item.path.includes("?");
  const routeHasQuery = activeRoute && activeRoute.includes("?");
  const isActive = hasOwnQuery
    ? activeRoute === item.path
    : !routeHasQuery && (activePath === pathBase ||
      (pathBase && pathBase !== "/home/" && activePath.startsWith(pathBase.replace(/\/$/, ""))));

  const handleClick = () => {
    if (!item.path) return;
    if (item.external) {
      const backendBase = String(getApiSettings().apiBase || "").replace(/\/$/, "");
      const target = `${backendBase}${item.path.startsWith("/") ? item.path : `/${item.path}`}`;
      window.location.href = target;
      return;
    }
    navigate(item.path);
  };

  // A red dot means "something is sitting here waiting for you". Deliberately
  // no number: a count would have to be read to learn whether it matters,
  // whereas a dot that is either there or not is answered at a glance. When the
  // sidebar is collapsed it rides the corner of the icon, because that is the
  // only thing still on screen.
  const showAlert = Boolean(item.badge);
  // "urgent" is a step up from the dot above: not "something is waiting",
  // but "something here needs attention right now" — the whole row turns
  // red, active-state background included, instead of a corner mark.
  const urgent = Boolean(item.urgent);
  const restColor = urgent ? "var(--red-50)" : "var(--blue-50)";
  const activeColor = urgent ? "var(--red-100)" : "var(--blue-100)";

  return (
    <button
      onClick={handleClick}
      title={showAlert && collapsed ? `${item.label} — waiting on you` : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        width: "100%", paddingRight: 12, paddingTop: 8, paddingBottom: 8,
        borderRadius: depth === 0 ? 8 : 13.6,
        border: "none",
        background: isActive ? activeColor : (urgent ? "var(--red-50)" : "transparent"),
        cursor: "pointer", textAlign: "left",
        paddingLeft: depth === 0 ? 12 : 32,
        transition: "background 0.12s",
        position: "relative",
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = restColor; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = urgent ? "var(--red-50)" : "transparent"; }}
    >
      <span style={{ flexShrink: 0, display: "flex", position: "relative" }}>
        {item.isDot ? <IconDot /> : item.icon ? item.icon : (
          <span style={{ width: collapsed ? 20 : 16, display: "block" }} />
        )}
        {showAlert && collapsed && (
          <span className="Atg-Nav-Alert" style={{ position: "absolute", top: -2, right: -3 }} />
        )}
      </span>
      {!collapsed && (
        <span style={{
          flex: 1, fontSize: item.quiet ? 11 : 14, fontWeight: item.quiet ? 400 : 500, lineHeight: item.quiet ? "14px" : "20px",
          color: item.quiet ? "var(--c-400)" : (urgent ? (isActive ? "var(--red-700)" : "var(--red-600)") : (isActive ? "var(--blue-700)" : "var(--c-500)")),
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {item.label}
        </span>
      )}
      {showAlert && !collapsed && (
        <span className="Atg-Nav-Alert" style={{ flexShrink: 0 }} aria-label="Waiting on you" />
      )}
    </button>
  );
}

// ─── AutoLogoutOnStuck ───────────────────────────────────────────────────────
// Wraps The Loading Screen. If The App Is Still Loading After TIMEOUT_MS, keep
// the existing session and let the user retry. Only a real 401 clears auth.
const STUCK_TIMEOUT_MS = 25000;
function AutoLogoutOnStuck({ onRetry, children }) {
  const { useEffect, useState } = React;
  const [stuck, setStuck] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  useEffect(() => {
    setStuck(false);
    const timer = setTimeout(() => setStuck(true), STUCK_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [onRetry, retryVersion]);
  const handleRetry = () => {
    setStuck(false);
    setRetryVersion((value) => value + 1);
    onRetry();
  };
  if (stuck) {
    return (
      <div className="Loading-View">
        <div className="Loading-Card">
          <AlertTriangle size={22} />
          <h2>Connection problem</h2>
          <p>The intranet is taking longer than expected to load. Your session was not logged out.</p>
          <button className="Primary-Button" onClick={handleRetry}>Retry</button>
        </div>
      </div>
    );
  }
  return children;
}

// ─── App ─────────────────────────────────────────────────────────────────────
// Routes a person with no account can open. Keep this list short and obvious:
// anything on it is reachable by anyone who has the URL.
const ANONYMOUS_ROUTES = ["/onboarding/culture", "/join/"];

function App() {
  const [settings, setSettings] = useState(getApiSettings);
  const [route, setRoute] = useState(() => window.location.pathname + window.location.search);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const path = route.split("?")[0];

  const isLoginRoute = path.startsWith("/login");
  // Offer emails sent before 9 Sep 2026 link to /offer/accept/<token>. The old
  // page behind it has been retired; the joining flow is the only way to accept.
  const isOldOfferRoute = path.startsWith("/offer/accept/");

  // "Signed in" is the server's session cookie; this mark only says the browser signed in, and
  // a 401 from any call clears it (endSession).
  const hasAuth = Boolean(settings.signedIn);
  const { data: loaded, loading, errors, apiOnline, reload } = useIntranetData(reloadKey, hasAuth && !isLoginRoute && !isOldOfferRoute);
  // Suggested Tasks opens for anyone with a suggestion to decide, not only for page holders: the
  // server answers, and the menu and the route follow it (Products/axon).
  const showSuggestedTasks = useSuggestedTasksMenu(hasAuth && !isLoginRoute && !isOldOfferRoute && Boolean(loaded?.me));
  const data = useMemo(() => withSuggestedTasksAccess(loaded, showSuggestedTasks), [loaded, showSuggestedTasks]);

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname + window.location.search);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((p, opts) => {
    if (opts?.replace) window.history.replaceState(null, "", p);
    else window.history.pushState(null, "", p);
    setRoute(window.location.pathname + window.location.search);
  }, []);

  // Entry 20 review: outside a support session the operator belongs in their own company. When a
  // session ends without "Leave" (closed in another tab, or it ran out) the stored company id went
  // stale and the screen kept naming the company just left. Put it back and re-read who they are;
  // once per stale company, so a server that disagrees cannot start a reload loop.
  const staleFixedFor = useRef("");
  useEffect(() => {
    const me = data?.me;
    const own = me?.user?.ownCompanyId;
    if (!me?.user?.isOperator || me?.support || !own) return;
    const active = String(me?.activeTenant?.id ?? "");
    if (String(getApiSettings().tenantId) === String(own) && active === String(own)) return;
    returnToOwnCompany(own);
    if (active !== String(own) && staleFixedFor.current !== active) {
      staleFixedFor.current = active;
      reload(["me"]);
    }
  }, [data?.me, reload]);

  useEffect(() => {
    const currentEmployee = resolveActiveEmployee(data);
    const nextEmployeeId = currentEmployee?.id ? String(currentEmployee.id) : "";
    if (nextEmployeeId && nextEmployeeId !== String(selectedEmployeeId || "")) {
      setSelectedEmployeeId(nextEmployeeId);
    }
    if (!nextEmployeeId && selectedEmployeeId) {
      setSelectedEmployeeId("");
    }
  }, [data.employees, data.me, selectedEmployeeId]);

  // ── Auto-Logout On 401 From Any API Call In The Entire App ──────────────────
  // The API Client Fires 'Auth:Unauthorized' For authentication failures only.
  // This Catches Expired Sessions Triggered From LMS, HRMS, Or Any Other Screen.
  useEffect(() => {
    const handleUnauthorized = () => {
      if (!hasAuth || isLoginRoute) return;
      endSession();
    };
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, [hasAuth, isLoginRoute]);

  // ── Also Catch 401 Surfaced Through The Bulk-Load Errors Array ───────────────
  useEffect(() => {
    if (!hasAuth || isLoginRoute) return;
    const hasAuthError = errors.some((e) => e.status === 401);
    if (hasAuthError) endSession();
  }, [errors, hasAuth, isLoginRoute]);

  // ── Visibility Heartbeat — Validate Session When User Returns To Tab ─────────
  // Covers: Server Deletes Session While Tab Is idle/backgrounded.
  // On Visibilitychange → Visible, Ping /Users/Auth/Me/. Only a 401 fires the
  // Auth:Unauthorized event (already wired above) and clears local auth.
  // We Debounce To Avoid Firing If The Tab Flashes Visible For < 300ms.
  useEffect(() => {
    if (!hasAuth || isLoginRoute) return;
    let debounceTimer = null;
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        apiGet("/Users/Auth/Me/").catch(() => {
          // 401 is already handled by the auth:unauthorized event in Client.js.
        });
      }, 300);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      clearTimeout(debounceTimer);
    };
  }, [hasAuth, isLoginRoute]);

  // ── Cross-Tab Storage Sync — Instant Logout When Auth Is Cleared Elsewhere ───
  // Covers: Sign-Out In Another Tab / DevTools / Script.
  // The 'Storage' Event Fires In ALL Tabs EXCEPT The One That Made The Change.
  // If The Signed-In Mark Disappears, Force Logout In This Tab Immediately.
  useEffect(() => {
    if (!hasAuth) return;
    const handleStorage = (event) => {
      if (event.key === "intranet.signedIn" && !event.newValue) {
        // Auth Was Cleared In Another Tab — Sync Logout Here Too
        endSession();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [hasAuth]);

  const login = (path = "/home/") => { setSettings(getApiSettings()); setReloadKey((v) => v + 1); navigate(path); };
  const retryAppLoad = useCallback(() => setReloadKey((v) => v + 1), []);
  const logout = async () => {
    try { await apiPost("/Users/Auth/Logout/"); } catch {}
    endSession();
  };

  // ── Redirects ────────────────────────────────────────────────────────────────
  // Done in an effect, after every hook has run, never while rendering. A logged-in user who types
  // /login/ in the URL bar is sent home instead of getting the login form while the session is valid.
  const redirecting = isOldOfferRoute || (hasAuth && isLoginRoute);
  useEffect(() => {
    if (isOldOfferRoute) window.location.replace("/join/" + path.replace("/offer/accept/", ""));
    else if (hasAuth && isLoginRoute) navigate("/home/");
  }, [isOldOfferRoute, hasAuth, isLoginRoute, path, navigate]);
  if (redirecting) return null;

  // Candidate-facing pages must render for people who are NOT signed in. A
  // candidate has no account yet — that is the entire point of them — so
  // sending them to the login screen defeats the page. Checked here, after
  // every hook has run, so the hook order never changes between renders.
  if (path.startsWith("/join/")) return <JoiningScreen />;
  if (path.startsWith("/onboarding/culture")) return <CultureCheckScreen />;

  if (!hasAuth) return <LoginScreen settings={settings} onLogin={login} />;

  // Wait for the page-access grid before rendering the shell, so nobody sees a
  // sidebar with items missing while it is still in flight.
  if (loading || !hasPageAccess(data?.me)) {
    return (
      <AutoLogoutOnStuck onRetry={retryAppLoad}>
        <LoadingView />
      </AutoLogoutOnStuck>
    );
  }

  const commonProps = { data, settings, selectedEmployeeId, reload, navigate, loading };

  // Old addresses (Entry 20): the admin panel is /admin/, and Plan and Plugins is now its
  // "Modules and add-ons" section.
  if (path.startsWith("/admin-panel")) return <MovedTo navigate={navigate} to="/company-admin/" />;
  if (path.startsWith("/plan-and-plugins")) return <MovedTo navigate={navigate} to="/company-admin/?section=modules" />;
  // The company admin panel is its own full-screen layout (own sidebar + Exit). The old
  // /admin/change-password/ address is still the Change Password screen (Entry 20 review), so it
  // is left to the router below instead of opening the panel.
  // /company-admin/, not /admin/: on the servers nginx sends /admin/ to Django's own admin, so a
  // refresh or a shared link there never reached this screen (found on dev, Entry 22).
  const isAdminPanelPath = path === "/company-admin" || path.startsWith("/company-admin/");
  if (isAdminPanelPath && (data.me?.user?.isCompanyAdmin || false)) {
    return <AdminPanelScreen data={data} navigate={navigate} logout={logout} reload={reload} />;
  }

  return (
    <AppShell route={route} navigate={navigate} data={data} apiOnline={apiOnline} loading={loading} logout={logout} errors={errors} reloadData={reload}>
      {path.startsWith("/profile")
        ? <ProfileScreen data={data} onLogout={logout} reload={reload} navigate={navigate} />
        : <RouteRenderer route={route} {...commonProps} />}
    </AppShell>
  );
}

// Define Constants Outside The Hook So They Don'T Change On Re-Renders
// Define Constants Outside The Hook So They Don'T Change On Re-Renders
const PRIORITY_KEYS = ["me", "employees", "departments", "positions", "skills", "projects", "tasks"];
const REALTIME_KEYS = ["me", "employees", "departments", "subDepartments", "positions", "domains", "departmentMemberships", "skills", "userSkills", "payProfiles", "bankAccounts", "paymentSnapshots", "leavePolicies", "leaveBalances", "leaveRequests", "leaveOverview", "notifications", "dailyStatus", "tasks", "projects", "teamAssignments", "financeDashboard", "lmsLeads", "learningPaths", "learningModules", "learningAssignments", "leadQueueSnapshots", "revenueSnapshots", "offers", "issues", "enterpriseRoles", "enterpriseRoleAssignments", "genericHtmlTemplates", "delays"];
const POLL_KEYS = ["me", "dailyStatus", "notifications", "tasks"];

// Which page governs each resource below — checked against the page-access
// grid on `me` before firing that resource's request at all, on the initial
// load (REALTIME_KEYS). Found by reading real traffic (Endpoint Usage,
// 2026-10-07): every one of these was fetched for every signed-in employee
// on every load regardless of whether they could ever see it, so most
// employees' browsers were firing a guaranteed-403 request on every single
// page load — not a security problem (the server was always the real
// gate), just pure waste, on both ends. Only the resources actually proven
// wasteful this way are listed; a resource with no entry here is still
// fetched unconditionally exactly as before, not newly restricted. `me`
// itself can't gate its own fetch (nothing is known yet before it
// answers), so this only takes effect from the second load onward — which
// is also the only one that matters, since the first load's `me` fetch is
// what makes every later one possible to gate at all.
const RESOURCE_PAGE_GATE = {
  departmentMemberships: "hrms",
  domains: "hrms",
  skills: "hrms",
  userSkills: "hrms",
  subDepartments: "hrms",
  payProfiles: "finance",
  paymentSnapshots: "finance",
  financeDashboard: "finance",
  leavePolicies: "leave_requests",
  projects: "project_management",
  teamAssignments: "project_management",
  delays: "delay_management",
  revenueSnapshots: "sales_performance",
  learningAssignments: "junior_onboarding",
  leadQueueSnapshots: "sales_crm",
  offers: "send_offer_letter",
  genericHtmlTemplates: "send_certificate",
  enterpriseRoles: "admin_panel",
  enterpriseRoleAssignments: "admin_panel",
};

function useIntranetData(reloadKey, enabled) {
  const [state, setState] = useState({ data: {}, loading: true, errors: [], apiOnline: false });
  const hasInitiallyLoaded = useRef(false);
  const loadVersion = useRef(0);
  const keyVersion = useRef({});
  const pollingRef = useRef(null);
  const enabledRef = useRef(enabled);
  const firstMount = useRef(true);
  enabledRef.current = enabled;
  // Mirrors state.data so `load` can read the latest `me` synchronously when
  // deciding what to fetch next, without depending on `state` and
  // re-creating itself (and the polling interval it feeds) every render.
  const dataRef = useRef(state.data);
  dataRef.current = state.data;

  const applyPayload = useCallback((nextData, key, mode, payload) => {
    const listPayload = mode === "list" ? unpackList(payload) : payload;
    if (key === "financeDashboard") {
      nextData.financeRows = payload?.user_list || [];
      nextData.financeDashboard = payload;
    } else if (key === "lmsLeads") {
      nextData.lmsLeads = payload;
      nextData.leadRows = payload?.results || [];
      nextData.leadOriginCounts = payload?.origin_counts || {};
    } else if (key === "departments") {
      nextData.departments = listPayload;
      // `data.departments` keeps every row, archived included — resolving an
      // employee's (possibly former) department name to display still needs
      // those. Every PICKER should read this derived list instead: the one
      // place that decides "archived means don't offer it," computed once
      // from the same fetch so it can never drift from `data.departments`.
      nextData.activeDepartments = (listPayload || []).filter((department) => !department.is_archived);
    } else {
      nextData[key] = listPayload;
    }
  }, []);

const load = useCallback(async (keysFilter, useCache = false) => {
     if (!enabledRef.current) { hasInitiallyLoaded.current = false; return; }
     const myVersion = ++loadVersion.current;
     let subset;
     if (Array.isArray(keysFilter) && keysFilter.length) {
       subset = endpointMap.filter(([key]) => keysFilter.includes(key));
     } else {
       subset = endpointMap.filter(([key]) => REALTIME_KEYS.includes(key));
     }
     const isSmallReload = Array.isArray(keysFilter) && keysFilter.length < 20;
     const isPartial = isSmallReload && subset.length !== endpointMap.length;
     
     setState((cur) => ({
       data: isPartial || hasInitiallyLoaded.current ? cur.data : {},
       loading: !isPartial ? true : cur.loading,
       errors: isPartial ? cur.errors : [],
       apiOnline: cur.apiOnline,
     }));

     // `me` (and therefore the page-access grid) is only known from the
     // previous successful load onward — nothing to gate against on the
     // very first call, so everything still goes out exactly as before
     // until then.
     const me = dataRef.current.me;
     const toFetch = me
       ? subset.filter(([key]) => {
           const page = RESOURCE_PAGE_GATE[key];
           return !page || canRead(me, page);
         })
       : [...subset];

     claimKeys(keyVersion.current, toFetch.map(([key]) => key), myVersion);

     const tag = ([key, p, mode]) =>
       fetchAllPages(apiGet, p).then(
         (payload) => { if (useCache) setCache(key, payload); return { key, payload, mode }; },
         (err) => { err._ek = key; err._em = mode; throw err; }
       );

     const results = await Promise.allSettled(toFetch.map(tag));

     setState((cur) => {
        const nextData = { ...cur.data };
        const requestErrors = isPartial && Array.isArray(keysFilter) ? cur.errors.filter((err) => !keysFilter.includes(err.key)) : [];
        results.forEach((result) => {
          let key, mode;
          // A newer load has asked for this resource since; its answer is the one to keep.
          const answeredKey = result.status === "fulfilled" ? result.value.key : result.reason?._ek;
          if (answeredKey && !isLatest(keyVersion.current, answeredKey, myVersion)) return;
          if (result.status === "fulfilled") { 
            key = result.value.key; 
            mode = result.value.mode; 
            applyPayload(nextData, key, mode, result.value.payload); 
          }
          else { 
            key = result.reason._ek; 
            mode = result.reason._em; 
            requestErrors.push({ key, status: result.reason?.status, message: result.reason?.message || "Request Failed" }); 
            if (!isPartial && key) nextData[key] = mode === "list" ? [] : null; 
          }
        });
        const half = toFetch.length / 2;
        const failCount = requestErrors.filter((e) => !e.key || e.status !== 401).length;
        return { data: nextData, loading: false, errors: requestErrors, apiOnline: isPartial ? cur.apiOnline : failCount < half };
      });
      hasInitiallyLoaded.current = true;
   }, [applyPayload]);

   // Initial Load — Single Shot
   useEffect(() => {
     if (!enabled) return;
     if (firstMount.current) { 
       firstMount.current = false; 
       hasInitiallyLoaded.current = false; 
     }
     load(REALTIME_KEYS);
   }, [reloadKey, enabled, load]);

   // Polling Effect
   useEffect(() => {
     if (!enabled) { 
       if (pollingRef.current) { 
         clearInterval(pollingRef.current); 
         pollingRef.current = null; 
       } 
       return; 
     }
     pollingRef.current = setInterval(() => { pollIfVisible(load, POLL_KEYS); }, 30000);
     return () => { 
       if (pollingRef.current) { 
         clearInterval(pollingRef.current); 
         pollingRef.current = null; 
       } 
     };
   }, [enabled, load]);

   // Visibility Change Effect
   useEffect(() => {
     if (!enabled) return;
     const onVisible = () => { if (document.visibilityState === "visible") load(["me"]); };
     document.addEventListener("visibilitychange", onVisible);
     return () => document.removeEventListener("visibilitychange", onVisible);
   }, [enabled, load]);

   return { ...state, reload: load };
}

// ΓöÇΓöÇΓöÇ App Shell ΓÇö Figma Builder Sidebar + Existing Topbar Logic ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
function AppShell({ children, route, navigate, data, apiOnline, loading, logout, errors, reloadData }) {
  const activePath = route.split("?")[0];
  const activeItem = navItems.find((item) => activePath === item.path || (item.path !== "/home/" && activePath.startsWith(item.path.replace(/\/$/, ""))));
  const pageTitle = activePath.startsWith("/profile") ? "Profile" : activeItem?.label || "Home";

  const user = data.me?.user || data.me?.account || data.me || {};
  const employee = resolveActiveEmployee(data) || {};
  const displayName = employee.display_name || employee.displayName || user.fullName || user.full_name || user.username || "";
  const initials = displayName ? String(displayName).split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() : "";
  // An auditor-only account is refused everywhere outside /audit by design: no banner for that.
  const visibleErrors = data.me?.auditorOnly ? [] : (errors.filter((item) => item.status && item.status !== 401 && item.status !== 403).slice(0, 1));

  const isSuper = data.me?.user?.isCompanyAdmin || false;

  // Red dot on Admin Panel when an integration is down — polled, not fetched
  // on every render, and only for superusers (the only people who can even
  // reach the page it's warning about).
  const [integrationsDown, setIntegrationsDown] = useState(false);
  const [leaveError, setLeaveError] = useState("");
  useEffect(() => {
    if (!isSuper) return;
    let cancelled = false;
    const poll = () => apiGet("/Users/admin-panel/integrations-health/")
      .then((res) => { if (!cancelled) setIntegrationsDown(!!res.any_down); })
      .catch(() => {});
    poll();
    const id = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isSuper]);

  // Same idea as the integrations dot above: poll rather than re-fetch on every
  // render, and only ask at all if this user can actually reach the page. The
  // endpoint returns a bare count, not the documents, because rendering a dot
  // should not be pulling file names and URLs across the wire.
  const [idsWaiting, setIdsWaiting] = useState(false);
  const canReviewIds = userCanSee("IDs Waiting", data.me);
  useEffect(() => {
    if (!canReviewIds) { setIdsWaiting(false); return; }
    let cancelled = false;
    const poll = () => apiGet("/MainApp/joining-reviews/count")
      .then((res) => { if (!cancelled) setIdsWaiting((res?.pending || 0) > 0); })
      .catch(() => {});
    poll();
    const id = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [canReviewIds]);

  // Delay Management turns the whole nav item red, not just a dot, while any
  // delay is Active — "delays" is already kept live via REALTIME_KEYS, so no
  // extra poll is needed here. The manual delay form (the only thing that
  // ever set status="Active") was retired 2026-10-07 — acknowledging a late
  // sprint logs status="Acknowledged", not "Active" — so this now only fires
  // off old pre-retirement rows nobody resolved yet, not a live signal.
  const hasActiveDelay = (data.delays || []).some((d) => d.status === "Active");

  const [calendarReconnect, setCalendarReconnect] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const poll = () => apiGet("/Banao/calendar/connections/mine/")
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setCalendarReconnect(list.some((row) =>
          row?.provider === "google" && row?.calendar_email && row?.sync_status !== "connected"
        ));
      })
      .catch(() => {});
    poll();
    const id = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.id]);

  const builtNav = buildNavItems(activePath, data.me, integrationsDown, idsWaiting, hasActiveDelay, calendarReconnect);
  // Sidebar collapse survives refreshes — persisted per browser via localStorage.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("atg.nav.collapsed") === "1"; } catch { return false; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; }

        .Atg-App { display: flex; height: 100vh; overflow: hidden; font-family: 'Inter', system-ui, sans-serif; background: var(--app-bg); }

        /* ΓöÇΓöÇ Sidebar ΓöÇΓöÇ */
        .Atg-Sidebar {
          width: 278px; min-width: 278px; height: 100vh;
          background: var(--app-bg);
          border-right: 1px solid var(--c-200);
          display: flex; flex-direction: column; overflow: hidden;
          transition: width 0.2s, min-width 0.2s;
        }
        .Atg-Sidebar.Collapsed {
          width: 80px; min-width: 80px;
        }

        /* Red dot: something is waiting on you in here. Slow and soft on
           purpose — a fast blink in permanent peripheral vision is an
           irritant, and this sits on screen all day. The ring keeps it
           legible against both the hover and the active row tint. */
        .Atg-Nav-Alert {
          width: 8px; height: 8px; border-radius: 50%;
          background: #E5484D;
          box-shadow: 0 0 0 2px var(--app-bg);
          animation: Atg-Nav-Alert-Pulse 2s ease-in-out infinite;
        }
        @keyframes Atg-Nav-Alert-Pulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.3; }
        }
        /* Anyone who has asked their machine to reduce motion still needs to
           see that something is waiting — hold it solid rather than hide it. */
        @media (prefers-reduced-motion: reduce) {
          .Atg-Nav-Alert { animation: none; opacity: 1; }
        }

        /* Brand */
        .Atg-Brand {
          display: flex; align-items: center; justify-content: space-between;
          padding: 24px 16px 0;
          margin-bottom: 24px;
          flex-shrink: 0;
        }
        .Atg-Brand-Left { display: flex; align-items: center; gap: 8px; }
        .Atg-Brand-Name {
          font-size: 24px; font-weight: 500; line-height: 24px;
          color: var(--c-900);
          font-family: sans-serif;
        }
        .Atg-Sidebar.Collapsed .Atg-Brand-Name { display: none; }
        .Atg-Sidebar.Collapsed .Atg-Brand { justify-content: center; padding: 24px 8px 0; }
        .Atg-Sidebar.Collapsed .Atg-Brand-Left { gap: 0; }

        .Atg-Collapse-Btn {
          width: 36px; height: 36px; border-radius: 8px;
          background: transparent;
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: var(--c-500); font-family: 'Inter', sans-serif;
          flex-shrink: 0; transition: background 0.15s, color 0.15s;
        }
        .Atg-Collapse-Btn:hover { background: var(--blue-50); color: var(--blue-700); }
        .Atg-Sidebar.Collapsed .Atg-Collapse-Btn { margin-left: auto; margin-right: auto; }

        /* Nav Scroll */
        .Atg-Nav {
          flex: 1; overflow-y: auto; overflow-x: hidden;
          padding: 0 16px 24px;
          display: flex; flex-direction: column; gap: 4px;
        }
        .Atg-Nav::-webkit-scrollbar { width: 4px; }
        .Atg-Nav::-webkit-scrollbar-track { background: transparent; }
        .Atg-Nav::-webkit-scrollbar-thumb { background: var(--blue-100); border-radius: 2px; }
        .Atg-Sidebar.Collapsed .Atg-Nav { padding: 0 8px 24px; }

        /* Subtle section labels that group the nav by function */
        .Atg-Nav-Section {
          font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
          text-transform: uppercase; color: var(--c-400);
          padding: 16px 12px 6px; margin-top: 6px;
          border-top: 1px solid var(--c-100); user-select: none;
        }
        .Atg-Sidebar.Collapsed .Atg-Nav-Section {
          margin: 8px 8px 2px; padding: 8px 0 0; height: 0;
          color: transparent; border-top: 1px solid var(--c-200); overflow: hidden;
        }

        /* The quiet "Terms"-style footer link every platform tucks under its
           own nav — deliberately tiny and low-contrast, not a real nav item,
           so it never competes with anything above it. */
        .Atg-Sidebar-Footer {
          padding: 10px 16px 14px; border-top: 1px solid var(--c-100);
        }
        .Atg-Sidebar-Footer button {
          background: none; border: none; cursor: pointer; padding: 0;
          font-size: 11px; color: var(--c-400); letter-spacing: 0.01em;
        }
        .Atg-Sidebar-Footer button:hover { color: var(--c-600); text-decoration: underline; }
        .Atg-Sidebar.Collapsed .Atg-Sidebar-Footer { display: none; }

        /* ΓöÇΓöÇ Main ΓöÇΓöÇ */
        .Atg-Main { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }

        /* Topbar */
        .Atg-Topbar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 24px; height: 56px;
          background: var(--surface); border-bottom: 1px solid var(--c-200);
          flex-shrink: 0; gap: 12px;
        }
        .Atg-Topbar-Title { display: flex; flex-direction: column; gap: 1px; }
        .Atg-Topbar-Title .crumb { font-size: 11px; color: var(--c-400); }
        .Atg-Topbar-Title strong { font-size: 15px; color: var(--c-900); font-weight: 600; }
        .Atg-Topbar-Actions { display: flex; align-items: center; gap: 8px; }

        .Sync-Badge {
          font-size: 11px; font-weight: 500;
          padding: 2px 8px; border-radius: 20px;
          background: var(--c-100); color: var(--c-500);
        }
        .Sync-Badge-Danger { background: var(--red-100); color: var(--red-600); }

        .Atg-User-Chip {
          display: flex; align-items: center; gap: 6px;
          padding: 4px 10px 4px 6px; border-radius: 20px;
          border: 1px solid var(--c-200); background: var(--surface);
          cursor: pointer; font-family: inherit; font-size: 13px;
          color: var(--c-700); font-weight: 500; transition: background 0.12s;
        }
        .Atg-User-Chip:hover, .Atg-User-Chip.Active { background: var(--c-100); }
        .Atg-User-Chip .Chip-Avatar {
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--blue-100); color: var(--blue-700);
          font-size: 11px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
        }

        .Atg-Icon-Btn {
          width: 32px; height: 32px; border-radius: 8px;
          border: none; background: transparent; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: var(--c-500); transition: background 0.12s, color 0.12s;
          position: relative;
        }
        .Atg-Icon-Btn:hover { background: var(--blue-50); color: var(--blue-700); }
        .Atg-Icon-Btn.Active { background: var(--blue-100); color: var(--blue-700); }

        /* Notification */
        .Atg-Notif-Bell { position: relative; }
        .Atg-Notif-Count {
          position: absolute; top: 2px; right: 2px;
          min-width: 16px; height: 16px; border-radius: 8px;
          background: var(--red-500); color: var(--surface);
          font-size: 10px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          padding: 0 3px; pointer-events: none;
        }
        .Atg-Notif-Popover {
          position: absolute; top: calc(100% + 8px); right: 0;
          width: 340px; background: var(--surface);
          border: 1px solid var(--c-200); border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10); z-index: 200; overflow: hidden;
        }
        .Atg-Notif-Head {
          display: flex; align-items: center; gap: 8px;
          padding: 12px 16px; border-bottom: 1px solid var(--c-100); flex-wrap: wrap;
        }
        .Atg-Notif-Head strong { font-size: 14px; color: var(--c-900); margin-right: auto; }
        .Atg-Notif-Head span { font-size: 12px; color: var(--c-400); }
        .Atg-Link-Btn { border: none; background: transparent; color: var(--blue-500); font-size: 12px; font-weight: 500; cursor: pointer; padding: 0; font-family: inherit; }
        .Atg-Link-Btn:hover { text-decoration: underline; }
        .Atg-Notif-List { max-height: 320px; overflow-y: auto; }
        .Atg-Notif-Item { display: flex; align-items: flex-start; gap: 12px; padding: 12px 16px; border-bottom: 1px solid var(--surface-soft); }
        .Atg-Notif-Item.Unread { background: var(--surface-soft); }
        .Atg-Notif-Item > div { flex: 1; min-width: 0; }
        .Atg-Notif-Item strong { display: block; font-size: 13px; color: var(--c-900); }
        .Atg-Notif-Item p { font-size: 12px; color: var(--c-500); margin: 2px 0; }
        .Atg-Notif-Item small { font-size: 11px; color: var(--c-400); }
        .Atg-Notif-Empty { padding: 24px; text-align: center; color: var(--c-400); font-size: 13px; }
        .Atg-Soft-Btn {
          border: 1px solid var(--c-200); background: var(--surface); border-radius: 6px;
          padding: 3px 8px; font-size: 12px; font-weight: 500;
          color: var(--c-700); cursor: pointer; white-space: nowrap;
          font-family: inherit; transition: background 0.12s;
        }
        .Atg-Soft-Btn:hover { background: var(--c-100); }

        .Section-Kicker { font-size: 12px; color: var(--c-500); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
        .Mini-Inp { height: 38px; border: 1px solid var(--c-300); border-radius: 8px; padding: 8px 12px; font-size: 14px; background: var(--surface); color: var(--c-900); width: 100%; font-family: inherit; }
        .Mini-Inp:focus { outline: none; border-color: var(--blue-500); box-shadow: 0 0 0 2px rgba(59,130,246,0.15); }
        .Auth-Alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; background: var(--red-50); color: var(--red-600); border: 1px solid var(--red-200); }
        .Auth-AlertOk { padding: 10px 14px; border-radius: 8px; font-size: 13px; background: var(--green-50); color: var(--green-600); border: 1px solid var(--green-100); }

        /* Error Banner */
        .Atg-Error-Bar {
          display: flex; align-items: center; gap: 8px;
          background: var(--red-50); border-bottom: 1px solid var(--red-200);
          padding: 10px 24px; color: var(--red-600); font-size: 13px; flex-shrink: 0;
        }

        /* Content */
        .Atg-Content { flex: 1; overflow-y: auto; overflow-x: hidden; background: var(--surface); }

        /* Mobile menu button + backdrop are hidden on desktop */
        .Atg-Mobile-Menu-Btn { display: none; }
        .Atg-Mobile-Backdrop { display: none; }

        /* ── Mobile: sidebar becomes an off-canvas overlay drawer ── */
        @media (max-width: 768px) {
          .Atg-Sidebar {
            position: fixed; top: 0; left: 0; height: 100vh; z-index: 300;
            transform: translateX(-100%); transition: transform 0.25s ease;
            box-shadow: 2px 0 18px rgba(0,0,0,0.20);
          }
          .Atg-Sidebar.MobileOpen { transform: translateX(0); }
          .Atg-Sidebar.Collapsed { width: 264px; min-width: 264px; }
          .Atg-Sidebar.Collapsed .Atg-Brand-Name { display: block; }
          .Atg-Main { width: 100%; }
          .Atg-Mobile-Menu-Btn {
            display: flex; align-items: center; justify-content: center;
            width: 36px; height: 36px; border-radius: 8px; border: none;
            background: transparent; color: var(--c-700); cursor: pointer; flex-shrink: 0;
          }
          .Atg-Mobile-Menu-Btn:hover { background: var(--blue-50); color: var(--blue-700); }
          .Atg-Mobile-Backdrop {
            display: block; position: fixed; inset: 0; z-index: 250;
            background: rgba(15,23,42,0.45);
          }
          .Atg-Topbar { padding: 0 12px; }
        }
      `}</style>

      <div className="Atg-App">
        {/* ΓöÇΓöÇ Sidebar ΓöÇΓöÇ */}
        {mobileNavOpen && <div className="Atg-Mobile-Backdrop" onClick={() => setMobileNavOpen(false)} />}
        <aside className={"Atg-Sidebar" + (collapsed ? " Collapsed" : "") + (mobileNavOpen ? " MobileOpen" : "")}>
          <div className="Atg-Brand">
            <div className="Atg-Brand-Left">
              <ATGLogo company={data?.me?.company} />
              <span className="Atg-Brand-Name">Intranet</span>
            </div>
            <button className="Atg-Collapse-Btn" onClick={() => setCollapsed((v) => { const next = !v; try { localStorage.setItem("atg.nav.collapsed", next ? "1" : "0"); } catch { /* private mode */ } return next; })} title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"} style={{ transform: collapsed ? "rotate(180deg)" : "rotate(0deg)", transition: "Transform 0.2s" }}>
              <ChevronLeft size={18} />
            </button>
          </div>

          <nav className="Atg-Nav">
            {builtNav.map((item) => (
              item.isSection ? (
                <div key={"section-" + item.label} className="Atg-Nav-Section" aria-hidden="true">
                  {collapsed ? "" : item.label}
                </div>
              ) : (
                <NavLink
                  key={item.path || item.label}
                  item={item}
                  depth={0}
                  activePath={activePath}
                  activeRoute={route}
                  navigate={(p) => { setMobileNavOpen(false); navigate(p); }}
                  collapsed={collapsed}
                />
              )
            ))}
          </nav>

        </aside>

        {/* ΓöÇΓöÇ Main ΓöÇΓöÇ */}
        <main className="Atg-Main">
          <header className="Atg-Topbar">
            <button className="Atg-Mobile-Menu-Btn" onClick={() => setMobileNavOpen(true)} aria-label="Open menu"><Menu size={20} /></button>
            <div className="Atg-Topbar-Title">
              <span className="crumb">Banao Intranet</span>
              <strong>{pageTitle}</strong>
            </div>
            <div className="Atg-Topbar-Actions">
              {loading && <span className="Sync-Badge">Syncing</span>}
              {/* A 403 means the page model correctly refused a bulk-load call the
                  user has no business seeing — that is working as intended, not a
                  connection problem. Counting it here made "Connection Issues"
                  fire for ordinary interns/non-full-timers the moment enforcement
                  went live, since the bulk endpoint list fetches dozens of pages
                  regardless of who's asking. Only real failures count now. */}
              {!loading && errors.filter((e) => e.status !== 401 && e.status !== 403).length >= 10 && <span className="Sync-Badge-Danger">Connection Issues</span>}
              <ThemeToggle />
              <NotificationBell notifications={data.notifications || []} navigate={navigate} reloadData={reloadData} />
              {/* The top-right is a single, meaningful control: your name opens
                  your profile. The bare logout arrow that used to sit here was
                  an accidental-logout trap — one stray click ended the session
                  with no confirmation, and it read as "does nothing" because no
                  menu opened. Logout still lives one click in, on the Profile
                  screen itself. */}
              {displayName && <button className={activePath.startsWith("/profile") ? "Atg-User-Chip Active" : "Atg-User-Chip"} onClick={() => navigate("/profile/")} title="Your profile">
                <span className="Chip-Avatar">{initials}</span>
                <b>{displayName}</b>
              </button>}
            </div>
          </header>

          {data.me?.impersonating && (
            <div className="Atg-Impersonate-Bar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 18px", background: "#5468FF", color: "#fff", fontSize: 13.5, fontWeight: 600 }}>
              <span>👁 Viewing as <b>{data.me?.user?.fullName || data.me?.user?.username}</b> — you're seeing exactly what they see (nav, access, masked contacts).</span>
              <button
                onClick={async () => {
                  // Navigate only if the session actually switched back. This
                  // used to swallow the error and redirect regardless, so a
                  // failed exit was indistinguishable from a successful one —
                  // you landed on /home/ still viewing as the other person,
                  // with nothing on screen saying why.
                  try {
                    await apiPost("/Users/admin-panel/stop-impersonate/", {});
                  } catch (e) {
                    alert(`Could not exit back to admin: ${e?.message || "request failed"}\n\nYou are still viewing as this user. Log out and back in if this persists.`);
                    return;
                  }
                  // Back to wherever "Login as" was started from (the admin
                  // panel's People list), not the target user's own /home/ —
                  // that was dropping the admin on a stranger's homepage
                  // with no obvious way back to the people list they were on.
                  let returnTo = "/home/";
                  try {
                    returnTo = sessionStorage.getItem("atg-impersonate-return-to") || returnTo;
                    sessionStorage.removeItem("atg-impersonate-return-to");
                  } catch (e) {}
                  window.location.href = returnTo;
                }}
                style={{ marginLeft: "auto", padding: "5px 14px", borderRadius: 7, border: "1px solid rgba(255,255,255,.6)", background: "rgba(255,255,255,.18)", color: "#fff", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                Exit — back to admin
              </button>
            </div>
          )}

          {data.me?.support && (
            // Entry 20, design A: the operator inside another company, during a recorded support session.
            <div className="Atg-Impersonate-Bar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 18px", background: "#B45309", color: "#fff", fontSize: 13.5, fontWeight: 600 }}>
              {/* Entry 20 review: only changes (and a few declared-sensitive reads) reach the trail, so say that, not "everything". */}
              <span>Platform support inside <b>{data.me?.activeTenant?.name}</b> until {new Date(data.me.support.endsAt).toLocaleTimeString()}. Changes you make are recorded in their trail.</span>
              {leaveError && <span role="alert" style={{ fontWeight: 700 }}>{leaveError}</span>}
              <button
                onClick={async () => {
                  setLeaveError("");
                  const left = await leaveSupport({
                    close: () => apiPost(`/EnterpriseCore/platform/support/${data.me.support.id}/close/`, {}),
                    restoreCompany: () => returnToOwnCompany(data.me?.user?.ownCompanyId),
                    goTo: () => { window.location.href = "/platform/"; },
                  });
                  if (!left) setLeaveError("Could not leave this company. Please try again.");
                }}
                style={{ marginLeft: "auto", padding: "5px 14px", borderRadius: 7, border: "1px solid rgba(255,255,255,.6)", background: "rgba(255,255,255,.18)", color: "#fff", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                Leave this company
              </button>
            </div>
          )}

          {visibleErrors.length > 0 && (
            <div className="Atg-Error-Bar">
              <AlertTriangle size={16} />
              <span>{visibleErrors[0].status === 403 ? "Current User Does Not Have Permission For One Or More Records." : visibleErrors[0].message}</span>
            </div>
          )}

          <div className="Atg-Content">{children}</div>
        </main>
      </div>
    </>
  );
}

// ΓöÇΓöÇΓöÇ Notification Bell ΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇΓöÇ
// ─── Theme Toggle (light / dark). Persists an explicit choice; with none set the
//     app follows the OS appearance (resolved by the pre-paint script in index.html). ───
function ThemeToggle() {
  const [theme, setTheme] = useState(() =>
    (typeof document !== "undefined" && document.documentElement.getAttribute("data-theme")) || "light"
  );
  const setMode = (next) => {
    document.documentElement.setAttribute("data-theme", next);
    try { localStorage.setItem("atg-theme", next); } catch (e) {}
    setTheme(next);
  };
  const next = theme === "dark" ? "light" : "dark";
  return (
    <button className="Atg-Icon-Btn" onClick={() => setMode(next)}
      title={`Switch to ${next} mode`} aria-label={`Switch to ${next} mode`}>
      {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}

function NotificationBell({ notifications = [], navigate, reloadData }) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [failedId, setFailedId] = useState("");
  const [markAllBusy, setMarkAllBusy] = useState(false);
  const [optimisticRead, setOptimisticRead] = useState(false);
  const ref = useRef(null);
  const unread = optimisticRead ? [] : notifications.filter((item) => !item.is_read);
  const rows = [...notifications].sort((l, r) => new Date(r.created_at || 0) - new Date(l.created_at || 0)).slice(0, 8);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDocClick); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const notificationPath = (item) => {
    // "You now have access to X" carries the page it opens, so one click goes
    // straight there instead of to the generic notifications list.
    const route = safeAppRoute(item.metadata?.route);
    if (route) return route;
    const t = String(item.resource_type || item.category || "").toLowerCase();
    const id = item.resource_id || item.metadata?.project || item.metadata?.project_id;
    if (t.includes("project") && id) return "/projects/";
    if (t.includes("leave")) return "/leave/apply/";
    return "/notifications/";
  };

  const review = async (item) => {
    setBusyId(String(item.id));
    setFailedId("");
    try {
      if (!item.is_read) await apiPost(`/MainApp/Notifications/${item.id}/mark-read/`, {});
      if (reloadData) reloadData(["notifications"]);
      navigate(notificationPath(item));
      setOpen(false);
    } catch {
      // The button says so; the person presses it again.
      setFailedId(String(item.id));
    } finally { setBusyId(""); }
  };

  const markAllRead = async () => {
    if (markAllBusy) return;
    setMarkAllBusy(true);
    setOptimisticRead(true);
    try {
      await apiPost("/MainApp/Notifications/mark-all-read/", {});
      setOptimisticRead(false);
      if (reloadData) reloadData(["notifications"]);
    } catch {
      setOptimisticRead(false);
    }
    setMarkAllBusy(false);
  };

  return (
    <div className="Atg-Notif-Bell" ref={ref}>
      <button className={open ? "Atg-Icon-Btn Active" : "Atg-Icon-Btn"} onClick={() => setOpen((v) => !v)} title="Notifications">
        <Bell size={16} />
        {unread.length > 0 && <span className="Atg-Notif-Count">{unread.length}</span>}
      </button>
      {open && (
        <div className="Atg-Notif-Popover">
          <div className="Atg-Notif-Head">
            <strong>Notifications</strong>
            <span>{unread.length} Unread</span>
            {unread.length > 0 && <button className="Atg-Link-Btn" onClick={markAllRead}>Mark All Read</button>}
            <button className="Atg-Link-Btn" onClick={() => { setOpen(false); navigate("/notifications/"); }}>View All</button>
          </div>
          <div className="Atg-Notif-List">
            {rows.map((item) => (
              <div key={item.id} className={item.is_read ? "Atg-Notif-Item" : "Atg-Notif-Item Unread"}>
                <div>
                  <strong>{item.title || item.category || "Notification"}</strong>
                  <p>{item.message || item.description || "Open This Notification."}</p>
                  <small>{item.created_at || ""}</small>
                </div>
                <button className="Atg-Soft-Btn" onClick={() => review(item)} disabled={busyId === String(item.id)}>
                  {busyId === String(item.id) ? "Opening" : failedId === String(item.id) ? "Try again" : "Review"}
                </button>
              </div>
            ))}
            {!rows.length && <div className="Atg-Notif-Empty">No Notifications Loaded.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
